// ============================================
// SERVICIOS.JS — Gestión operativa de servicios
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { OPC_DINERO } from './utils/dinero';

const PP = "'Poppins', sans-serif";
const COLOR_PRINCIPAL = '#5c9adb';
const COLOR_OSCURO = '#2f5f9e';
const COLOR_FONDO = '#f3f4f4';
const COLOR_GRIS = '#4e5357';
const COLOR_GRIS_CLARO = '#8a8f95';
const COLOR_VERDE = '#5cd4b0';
const COLOR_ROJO = '#e05c5c';
const COLOR_AMARILLO = '#dcde74';

const KEY_SERVICIOS = 'beep_servicios';
const KEY_USUARIOS = 'beep_usuarios';

const TIPOS_SERVICIO = {
  correctivo:    { label: 'Correctivo',    icono: '🛠️', color: '#e05c5c' },
  mantenimiento: { label: 'Mantenimiento', icono: '🔩', color: '#5c9adb' },
  instalacion:   { label: 'Instalación',   icono: '🏗️', color: '#5cd4b0' },
  levantamiento: { label: 'Levantamiento', icono: '📏', color: '#f4a261' },
  soporte_ti:    { label: 'Soporte TI',    icono: '💻', color: '#9b59b6' },
  monitoreo:     { label: 'Monitoreo',     icono: '👁️', color: '#2f5f9e' },
};

const ESTADOS = {
  nuevo:       { label: 'Nuevo',       color: '#888' },
  asignado:    { label: 'Asignado',    color: '#5c9adb' },
  en_camino:   { label: 'En camino',   color: '#f4a261' },
  en_sitio:    { label: 'En sitio',    color: '#dcde74' },
  en_proceso:  { label: 'En proceso',  color: '#9b59b6' },
  completado:  { label: 'Completado',  color: '#5cd4b0' },
  validado:    { label: 'Validado',    color: '#2f5f9e' },
  facturado:   { label: 'Facturado',   color: '#5cd4b0' },
  cancelado:   { label: 'Cancelado',   color: '#e05c5c' },
};

function cargarClientesBeep() {
  try { return JSON.parse(localStorage.getItem('beep_clientes') || '[]'); } catch { return []; }
}
function nombresClientesBeep() { return cargarClientesBeep().map(c => c.nombre); }
function sucursalesClienteBeep(nombre) {
  const c = cargarClientesBeep().find(x => x.nombre === nombre);
  return (c?.sucursales || []).map(s => s.nombre);
}

function SERVICIOS_DEMO() {
  const ahora = Date.now();
  return [
    {
      id: 1, folio: 'SRV-2026-001',
      tipo: 'correctivo', estado: 'en_proceso',
      cliente: 'Bodegas Alianza', sucursal: 'Sucursal Norte',
      contacto: '', telefono: '', direccion: '',
      descripcion: 'Cámara 3 sin imagen, posible cable dañado',
      equipoAfectado: 'Cámara IP exterior entrada principal',
      tecnicoAsignado: { id: 4, nombre: 'Isaías Méndez' },
      supervisorAsignado: null,
      fechaProgramada: new Date(ahora).toISOString(), horaProgramada: '09:00',
      fechaInicio: new Date(ahora - 600000).toISOString(), fechaFin: null, duracionMinutos: null,
      tiempoEstimadoMinutos: 90, costoServicio: 800,
      materialesUsados: [], fotos: [], reporte: '',
      firmaCliente: null, calificacion: null, notas: [],
      historial: [
        { fecha: new Date(ahora - 3600000).toISOString(), accion: 'Servicio creado' },
        { fecha: new Date(ahora - 1800000).toISOString(), accion: 'Técnico en camino' },
        { fecha: new Date(ahora - 900000).toISOString(),  accion: 'Llegó al sitio' },
        { fecha: new Date(ahora - 600000).toISOString(),  accion: 'Servicio iniciado' },
      ],
      origen: 'manual', origenRequerimiento: null,
      garantia: false, cobrar: true, montoTotal: 800,
    },
    {
      id: 2, folio: 'SRV-2026-002',
      tipo: 'mantenimiento', estado: 'asignado',
      cliente: 'Farma Plus', sucursal: 'Polanco',
      contacto: '', telefono: '', direccion: '',
      descripcion: 'Mantenimiento preventivo trimestral sistema CCTV',
      equipoAfectado: '8 cámaras + grabador',
      tecnicoAsignado: { id: 4, nombre: 'Isaías Méndez' },
      supervisorAsignado: null,
      fechaProgramada: new Date(ahora + 86400000).toISOString(), horaProgramada: '10:00',
      fechaInicio: null, fechaFin: null, duracionMinutos: null,
      tiempoEstimadoMinutos: 120, costoServicio: 1200,
      materialesUsados: [], fotos: [], reporte: '',
      firmaCliente: null, calificacion: null, notas: [],
      historial: [{ fecha: new Date().toISOString(), accion: 'Servicio programado' }],
      origen: 'manual', origenRequerimiento: null,
      garantia: false, cobrar: true, montoTotal: 1200,
    },
    {
      id: 3, folio: 'SRV-2026-003',
      tipo: 'correctivo', estado: 'nuevo',
      cliente: 'Abarrotes La Manita', sucursal: 'Local 1',
      contacto: '', telefono: '', direccion: '',
      descripcion: 'Red caída en área de cajas, no conecta ninguna PC',
      equipoAfectado: 'Switch principal y 3 PCs',
      tecnicoAsignado: null, supervisorAsignado: null,
      fechaProgramada: null, horaProgramada: null,
      fechaInicio: null, fechaFin: null, duracionMinutos: null,
      tiempoEstimadoMinutos: 60, costoServicio: 800,
      materialesUsados: [], fotos: [], reporte: '',
      firmaCliente: null, calificacion: null, notas: [],
      historial: [{ fecha: new Date().toISOString(), accion: 'Requerimiento recibido' }],
      origen: 'manual', origenRequerimiento: null,
      garantia: false, cobrar: true, montoTotal: 800,
    },
  ];
}

function cargarServicios() {
  try {
    const raw = localStorage.getItem(KEY_SERVICIOS);
    if (raw) return JSON.parse(raw);
  } catch {}
  const seed = SERVICIOS_DEMO();
  try { localStorage.setItem(KEY_SERVICIOS, JSON.stringify(seed)); } catch {}
  return seed;
}
function guardarServiciosLS(arr) {
  try { localStorage.setItem(KEY_SERVICIOS, JSON.stringify(arr)); } catch {}
}
function cargarUsuarios() {
  try { return JSON.parse(localStorage.getItem(KEY_USUARIOS) || '[]'); } catch { return []; }
}

function normalizarRol(rol) {
  if (!rol) return 'direccion';
  const r = String(rol).toLowerCase();
  if (r.includes('direcc')) return 'direccion';
  if (r.includes('admin')) return 'administracion';
  if (r.includes('superv')) return 'supervisor';
  if (r.includes('técnic') || r.includes('tecnic')) return 'tecnico';
  return 'direccion';
}

function iniciales(nombre) {
  return (nombre || '?').split(' ').filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('');
}

function siguienteFolio(arr) {
  const año = new Date().getFullYear();
  return `SRV-${año}-${String(arr.length + 1).padStart(3, '0')}`;
}

function fechaCorta(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
  } catch { return '—'; }
}

function esHoy(iso) {
  if (!iso) return false;
  const d = new Date(iso); const h = new Date();
  return d.getDate() === h.getDate() && d.getMonth() === h.getMonth() && d.getFullYear() === h.getFullYear();
}

function minutosTranscurridos(fechaInicioIso) {
  if (!fechaInicioIso) return 0;
  return Math.floor((Date.now() - new Date(fechaInicioIso).getTime()) / 60000);
}

function formatoMinutos(min) {
  if (min < 60) return `${min}m`;
  return `${Math.floor(min / 60)}h ${min % 60}m`;
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
function Servicios({ filtroTipo, rolUsuario, esCelular }) {
  const [servicios, setServicios] = useState(cargarServicios);
  const [usuarios] = useState(cargarUsuarios);
  const [tab, setTab] = useState('hoy');
  const [busqueda, setBusqueda] = useState('');
  const [modal, setModal] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => { guardarServiciosLS(servicios); }, [servicios]);
  // Re-render cada 30s para refrescar timers
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const rolKey = normalizarRol(rolUsuario);
  const puedeValidar = rolKey === 'direccion' || rolKey === 'supervisor' || rolKey === 'administracion';

  const tecnicosYSupervisores = useMemo(
    () => usuarios.filter(u => ['tecnico', 'supervisor'].includes(u.rol)),
    [usuarios]
  );

  const filtradas = useMemo(() => {
    let arr = servicios;
    if (filtroTipo) arr = arr.filter(s => s.tipo === filtroTipo);
    if (busqueda) {
      const q = busqueda.toLowerCase();
      arr = arr.filter(s =>
        (s.folio || '').toLowerCase().includes(q) ||
        (s.cliente || '').toLowerCase().includes(q) ||
        (s.descripcion || '').toLowerCase().includes(q)
      );
    }
    if (tab === 'hoy') {
      arr = arr.filter(s => esHoy(s.fechaProgramada) && !['completado', 'validado', 'facturado', 'cancelado'].includes(s.estado));
    } else if (tab === 'pendientes') {
      arr = arr.filter(s => (!s.fechaProgramada || new Date(s.fechaProgramada) > new Date()) && !['completado', 'validado', 'facturado', 'cancelado'].includes(s.estado));
    } else if (tab === 'campo') {
      arr = arr.filter(s => ['en_camino', 'en_sitio', 'en_proceso'].includes(s.estado));
    } else if (tab === 'historial') {
      arr = arr.filter(s => ['completado', 'validado', 'facturado', 'cancelado'].includes(s.estado));
    }
    return arr;
  }, [servicios, filtroTipo, busqueda, tab, tick]);

  const crearServicio = (data) => {
    const nuevo = {
      id: Date.now(),
      folio: siguienteFolio(servicios),
      ...data,
      estado: 'nuevo',
      fechaInicio: null, fechaFin: null, duracionMinutos: null,
      materialesUsados: [], fotos: [], reporte: '',
      firmaCliente: null, calificacion: null, notas: [],
      historial: [{ fecha: new Date().toISOString(), accion: 'Servicio creado' }],
      origen: 'manual', origenRequerimiento: null,
      montoTotal: data.cobrar ? data.costoServicio : 0,
    };
    setServicios([nuevo, ...servicios]);
    setModal(null);
  };

  const cambiarEstado = (id, nuevoEstado, datos = {}) => {
    setServicios(prev => prev.map(s => {
      if (s.id !== id) return s;
      const ahora = new Date().toISOString();
      const acc = ESTADOS[nuevoEstado]?.label || nuevoEstado;
      const upd = { ...s, estado: nuevoEstado, ...datos };
      upd.historial = [...(s.historial || []), { fecha: ahora, accion: acc }];
      if (nuevoEstado === 'en_proceso' && !s.fechaInicio) upd.fechaInicio = ahora;
      if (nuevoEstado === 'completado') {
        upd.fechaFin = ahora;
        if (s.fechaInicio) upd.duracionMinutos = Math.floor((Date.now() - new Date(s.fechaInicio).getTime()) / 60000);
      }
      return upd;
    }));
  };

  const tarjetaCard = { background: '#fff', borderRadius: 14, padding: esCelular ? 12 : 18, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' };
  const inputBase = { padding: '10px 12px', borderRadius: 10, border: '1px solid #d8dde3', fontFamily: PP, fontSize: 13, outline: 'none', background: '#fff', boxSizing: 'border-box' };
  const btnPrimario = { padding: '10px 18px', background: COLOR_PRINCIPAL, color: '#fff', border: 'none', borderRadius: 10, fontFamily: PP, fontSize: 13, fontWeight: 700, cursor: 'pointer' };

  const tabs = [
    { id: 'hoy', label: 'Hoy' },
    { id: 'pendientes', label: 'Pendientes' },
    { id: 'campo', label: 'En campo' },
    { id: 'historial', label: 'Historial' },
  ];

  const titulo = filtroTipo
    ? `${TIPOS_SERVICIO[filtroTipo]?.icono || '🔧'} Servicios — ${TIPOS_SERVICIO[filtroTipo]?.label || filtroTipo}`
    : '🔧 Servicios';

  return (
    <div style={{ fontFamily: PP, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`@keyframes pulseRojo { 0%,100% { box-shadow: 0 0 0 0 rgba(224,92,92,0.5); } 50% { box-shadow: 0 0 0 6px rgba(224,92,92,0); } }`}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: esCelular ? 20 : 24, fontWeight: 800, color: COLOR_GRIS }}>{titulo}</div>
          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 4 }}>{filtradas.length} en esta vista</div>
        </div>
        <button onClick={() => setModal({ modo: 'crear' })} style={btnPrimario}>+ Nuevo servicio</button>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '8px 18px', borderRadius: 12,
              background: tab === t.id ? COLOR_PRINCIPAL : '#fff',
              color: tab === t.id ? '#fff' : COLOR_OSCURO,
              border: `1.5px solid ${COLOR_PRINCIPAL}`,
              fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: PP,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={tarjetaCard}>
        <input
          type="text"
          placeholder="🔍 Buscar por folio, cliente o descripción..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          style={{ ...inputBase, width: '100%' }}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {filtradas.length === 0 ? (
          <div style={{ ...tarjetaCard, textAlign: 'center', padding: 40, color: COLOR_GRIS_CLARO, fontSize: 13 }}>
            No hay servicios en esta vista.
          </div>
        ) : filtradas.map(s => (
          <TarjetaServicio key={s.id} servicio={s} esCelular={esCelular} onAbrir={() => setModal({ modo: 'detalle', servicio: s })} />
        ))}
      </div>

      {modal?.modo === 'crear' && (
        <ModalNuevoServicio
          tecnicos={tecnicosYSupervisores}
          tipoInicial={filtroTipo}
          onCrear={crearServicio}
          onCerrar={() => setModal(null)}
        />
      )}
      {modal?.modo === 'detalle' && (
        <ModalDetalle
          servicio={servicios.find(s => s.id === modal.servicio.id) || modal.servicio}
          tecnicos={tecnicosYSupervisores}
          puedeValidar={puedeValidar}
          onCerrar={() => setModal(null)}
          onCambiarEstado={(estado, datos) => cambiarEstado(modal.servicio.id, estado, datos)}
          onActualizar={(cambios) => {
            setServicios(prev => prev.map(s => s.id === modal.servicio.id ? { ...s, ...cambios } : s));
          }}
        />
      )}
    </div>
  );
}

// ============================================
// TARJETA DE SERVICIO
// ============================================
function TarjetaServicio({ servicio, esCelular, onAbrir }) {
  const tipo = TIPOS_SERVICIO[servicio.tipo] || TIPOS_SERVICIO.correctivo;
  const estado = ESTADOS[servicio.estado] || ESTADOS.nuevo;
  const enProceso = servicio.estado === 'en_proceso';
  const transcurrido = enProceso ? minutosTranscurridos(servicio.fechaInicio) : 0;
  const excedido = enProceso && transcurrido > (servicio.tiempoEstimadoMinutos || 60);

  return (
    <div
      onClick={onAbrir}
      style={{
        background: '#fff', borderRadius: 12,
        boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
        display: 'flex', overflow: 'hidden', cursor: 'pointer',
        border: excedido ? `2px solid ${COLOR_ROJO}` : '2px solid transparent',
        animation: excedido ? 'pulseRojo 1.5s ease-in-out infinite' : 'none',
      }}
    >
      <div style={{ width: 6, background: tipo.color, flexShrink: 0 }} />
      <div style={{ flex: 1, padding: esCelular ? 12 : 14, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>{tipo.icono}</span>
            <div>
              <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 700 }}>{servicio.folio}</div>
              <div style={{ fontSize: 11, color: tipo.color, fontWeight: 700 }}>{tipo.label}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {excedido && (
              <span style={{ fontSize: 10, fontWeight: 800, background: COLOR_ROJO, color: '#fff', padding: '3px 8px', borderRadius: 10 }}>
                ⚠️ TIEMPO EXCEDIDO
              </span>
            )}
            <span style={{ fontSize: 10, fontWeight: 700, background: estado.color + '22', color: estado.color, padding: '4px 10px', borderRadius: 12 }}>
              {estado.label}
            </span>
          </div>
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_GRIS, marginBottom: 2 }}>
          {servicio.cliente}{servicio.sucursal ? ` — ${servicio.sucursal}` : ''}
        </div>
        <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {servicio.descripcion}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', fontSize: 11, color: COLOR_GRIS }}>
          {servicio.tecnicoAsignado && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%',
                background: COLOR_PRINCIPAL, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, fontWeight: 700,
              }}>{iniciales(servicio.tecnicoAsignado.nombre)}</div>
              <span>{servicio.tecnicoAsignado.nombre.split(' ')[0]}</span>
            </div>
          )}
          {servicio.fechaProgramada && (
            <span>📅 {fechaCorta(servicio.fechaProgramada)}{servicio.horaProgramada ? ` · ${servicio.horaProgramada}` : ''}</span>
          )}
          {enProceso && (
            <span style={{ color: excedido ? COLOR_ROJO : COLOR_OSCURO, fontWeight: 700 }}>
              ⏱ {formatoMinutos(transcurrido)} / {formatoMinutos(servicio.tiempoEstimadoMinutos)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// MODAL NUEVO SERVICIO (3 pasos)
// ============================================
function ModalNuevoServicio({ tecnicos, tipoInicial, onCrear, onCerrar }) {
  const [paso, setPaso] = useState(1);
  const [tipo, setTipo] = useState(tipoInicial || 'correctivo');
  const [cliente, setCliente] = useState('');
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [sucursal, setSucursal] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [equipoAfectado, setEquipoAfectado] = useState('');
  const [tecnicoId, setTecnicoId] = useState('');
  const [fechaProgramada, setFechaProgramada] = useState('');
  const [horaProgramada, setHoraProgramada] = useState('');
  const [tiempoEstimadoMinutos, setTiempoEstimadoMinutos] = useState(60);
  const [costoServicio, setCostoServicio] = useState(800);
  const [garantia, setGarantia] = useState(false);
  const [cobrar, setCobrar] = useState(true);
  const [grabando, setGrabando] = useState(false);
  const recRef = React.useRef(null);

  const sucursalesDisp = cliente ? sucursalesClienteBeep(cliente) : [];
  const clientesFilt = busquedaCliente
    ? nombresClientesBeep().filter(c => c.toLowerCase().includes(busquedaCliente.toLowerCase()))
    : [];
  const tecnico = tecnicos.find(t => t.id === tecnicoId);

  const inputBase = { padding: '10px 12px', borderRadius: 10, border: '1px solid #d8dde3', fontFamily: PP, fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' };
  const labelStyle = { fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 700, marginBottom: 4, display: 'block', textTransform: 'uppercase' };

  const puede1 = tipo && cliente && descripcion.trim();
  const puede2 = fechaProgramada || tipo === 'levantamiento';

  const toggleMic = () => {
    if (grabando) {
      try { recRef.current?.stop(); } catch {}
      setGrabando(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Tu navegador no soporta dictado por voz'); return; }
    const rec = new SR();
    rec.lang = 'es-MX';
    rec.continuous = true; rec.interimResults = true;
    rec.onresult = (e) => {
      let txt = '';
      for (let i = 0; i < e.results.length; i++) txt += e.results[i][0].transcript + ' ';
      setDescripcion(txt);
    };
    rec.onend = () => setGrabando(false);
    rec.onerror = () => setGrabando(false);
    recRef.current = rec;
    setGrabando(true);
    rec.start();
  };

  const submit = () => {
    onCrear({
      tipo, cliente, sucursal,
      contacto: '', telefono: '', direccion: '',
      descripcion: descripcion.trim(), equipoAfectado,
      tecnicoAsignado: tecnico ? { id: tecnico.id, nombre: tecnico.nombre } : null,
      supervisorAsignado: null,
      fechaProgramada: fechaProgramada ? new Date(fechaProgramada).toISOString() : null,
      horaProgramada: horaProgramada || null,
      tiempoEstimadoMinutos: Number(tiempoEstimadoMinutos) || 60,
      costoServicio: Number(costoServicio) || 0,
      garantia, cobrar,
    });
  };

  return (
    <div style={overlay}>
      <div style={{ ...modalCard, maxWidth: 520 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: COLOR_OSCURO }}>Nuevo servicio</div>
          <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>Paso {paso} de 3</div>
        </div>

        <div style={{ display: 'flex', gap: 4, marginBottom: 18 }}>
          {[1, 2, 3].map(n => (
            <div key={n} style={{ flex: 1, height: 4, borderRadius: 4, background: n <= paso ? COLOR_PRINCIPAL : '#e6e9ee' }} />
          ))}
        </div>

        {paso === 1 && (
          <div>
            <label style={labelStyle}>Tipo de servicio</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
              {Object.entries(TIPOS_SERVICIO).map(([k, info]) => (
                <button
                  key={k}
                  onClick={() => setTipo(k)}
                  style={{
                    padding: 12, borderRadius: 10,
                    background: tipo === k ? info.color : '#fff',
                    color: tipo === k ? '#fff' : COLOR_GRIS,
                    border: `1.5px solid ${info.color}`,
                    fontWeight: 700, fontSize: 11, cursor: 'pointer', fontFamily: PP,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  }}
                >
                  <span style={{ fontSize: 20 }}>{info.icono}</span>
                  {info.label}
                </button>
              ))}
            </div>

            <label style={{ ...labelStyle, marginTop: 12 }}>Cliente</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Buscar cliente..."
                value={cliente || busquedaCliente}
                onChange={(e) => { setCliente(''); setSucursal(''); setBusquedaCliente(e.target.value); }}
                style={inputBase}
              />
              {clientesFilt.length > 0 && !cliente && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0,
                  background: '#fff', border: `1px solid ${COLOR_PRINCIPAL}55`,
                  borderRadius: 8, marginTop: 2, zIndex: 5,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}>
                  {clientesFilt.map(c => (
                    <div
                      key={c}
                      onClick={() => { setCliente(c); setBusquedaCliente(''); setSucursal(''); }}
                      style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                    >
                      {c}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {cliente && sucursalesDisp.length > 0 && (
              <>
                <label style={{ ...labelStyle, marginTop: 12 }}>Sucursal</label>
                <select value={sucursal} onChange={(e) => setSucursal(e.target.value)} style={inputBase}>
                  <option value="">Selecciona...</option>
                  {sucursalesDisp.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </>
            )}

            <label style={{ ...labelStyle, marginTop: 12 }}>Descripción del problema</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
              <textarea
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                rows={3}
                style={{ ...inputBase, fontFamily: PP, resize: 'vertical' }}
                placeholder="Describe el problema..."
              />
              <button
                type="button"
                onClick={toggleMic}
                style={{
                  padding: '10px 12px',
                  background: grabando ? COLOR_ROJO : COLOR_PRINCIPAL,
                  color: '#fff', border: 'none', borderRadius: 10,
                  cursor: 'pointer', fontFamily: PP, fontSize: 16,
                }}
              >
                {grabando ? '⏹' : '🎙'}
              </button>
            </div>

            <label style={{ ...labelStyle, marginTop: 12 }}>Equipo afectado</label>
            <input value={equipoAfectado} onChange={(e) => setEquipoAfectado(e.target.value)} style={inputBase} placeholder="Ej: Cámara entrada principal" />
          </div>
        )}

        {paso === 2 && (
          <div>
            <label style={labelStyle}>Técnico asignado</label>
            <select value={tecnicoId} onChange={(e) => setTecnicoId(Number(e.target.value))} style={inputBase}>
              <option value="">Sin asignar</option>
              {tecnicos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>

            <label style={{ ...labelStyle, marginTop: 12 }}>Fecha programada</label>
            <input type="date" value={fechaProgramada} onChange={(e) => setFechaProgramada(e.target.value)} style={inputBase} />

            <label style={{ ...labelStyle, marginTop: 12 }}>Hora</label>
            <input type="time" value={horaProgramada} onChange={(e) => setHoraProgramada(e.target.value)} style={inputBase} />

            <label style={{ ...labelStyle, marginTop: 12 }}>Tiempo estimado (minutos)</label>
            <input type="number" min="1" value={tiempoEstimadoMinutos} onChange={(e) => setTiempoEstimadoMinutos(e.target.value)} style={inputBase} />

            <label style={{ ...labelStyle, marginTop: 12 }}>Costo del servicio (MXN)</label>
            <input type="number" min="0" value={costoServicio} onChange={(e) => setCostoServicio(e.target.value)} style={inputBase} />

            <div style={{ marginTop: 12, padding: 10, background: COLOR_FONDO, borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: COLOR_GRIS, cursor: 'pointer' }}>
                <input type="checkbox" checked={garantia} onChange={(e) => setGarantia(e.target.checked)} />
                Aplica garantía
              </label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: COLOR_GRIS, cursor: 'pointer' }}>
                <input type="checkbox" checked={cobrar} onChange={(e) => setCobrar(e.target.checked)} />
                Se cobra al cliente
              </label>
            </div>
          </div>
        )}

        {paso === 3 && (
          <div style={{ background: COLOR_FONDO, borderRadius: 12, padding: 16, fontSize: 12, color: COLOR_GRIS, lineHeight: 1.8 }}>
            <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 700, marginBottom: 6 }}>RESUMEN</div>
            <div><strong>Tipo:</strong> {TIPOS_SERVICIO[tipo].icono} {TIPOS_SERVICIO[tipo].label}</div>
            <div><strong>Cliente:</strong> {cliente}{sucursal ? ` — ${sucursal}` : ''}</div>
            <div><strong>Descripción:</strong> {descripcion}</div>
            {equipoAfectado && <div><strong>Equipo:</strong> {equipoAfectado}</div>}
            <div><strong>Técnico:</strong> {tecnico?.nombre || 'Sin asignar'}</div>
            <div><strong>Fecha:</strong> {fechaProgramada || '—'} {horaProgramada && `· ${horaProgramada}`}</div>
            <div><strong>Tiempo estimado:</strong> {tiempoEstimadoMinutos} min</div>
            <div><strong>Costo:</strong> ${Number(costoServicio).toLocaleString('es-MX', OPC_DINERO)} {!cobrar && '(no se cobra)'}</div>
            {garantia && <div><strong>Garantía:</strong> Sí</div>}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          {paso > 1 && (
            <button onClick={() => setPaso(paso - 1)} style={{ flex: 1, padding: 12, background: '#f1f3f6', border: 'none', borderRadius: 10, fontFamily: PP, fontWeight: 700, cursor: 'pointer' }}>
              Atrás
            </button>
          )}
          <button onClick={onCerrar} style={{ flex: 1, padding: 12, background: '#f1f3f6', border: 'none', borderRadius: 10, fontFamily: PP, fontWeight: 700, cursor: 'pointer' }}>
            Cancelar
          </button>
          {paso < 3 ? (
            <button
              onClick={() => setPaso(paso + 1)}
              disabled={paso === 1 ? !puede1 : !puede2}
              style={{
                flex: 1, padding: 12, background: COLOR_PRINCIPAL, color: '#fff',
                border: 'none', borderRadius: 10, fontFamily: PP, fontWeight: 700,
                cursor: 'pointer', opacity: (paso === 1 ? puede1 : puede2) ? 1 : 0.5,
              }}
            >
              Siguiente →
            </button>
          ) : (
            <button onClick={submit} style={{ flex: 1, padding: 12, background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 10, fontFamily: PP, fontWeight: 700, cursor: 'pointer' }}>
              Crear servicio
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// MODAL DETALLE
// ============================================
function ModalDetalle({ servicio, tecnicos, puedeValidar, onCerrar, onCambiarEstado, onActualizar }) {
  const [reporte, setReporte] = useState(servicio.reporte || '');
  const [nuevoMaterial, setNuevoMaterial] = useState('');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (servicio.estado === 'en_proceso') {
      const id = setInterval(() => setTick(t => t + 1), 30000);
      return () => clearInterval(id);
    }
  }, [servicio.estado]);

  const tipo = TIPOS_SERVICIO[servicio.tipo] || TIPOS_SERVICIO.correctivo;
  const estado = ESTADOS[servicio.estado] || ESTADOS.nuevo;
  const enProceso = servicio.estado === 'en_proceso';
  const transcurrido = enProceso ? minutosTranscurridos(servicio.fechaInicio) : 0;
  const excedido = enProceso && transcurrido > (servicio.tiempoEstimadoMinutos || 60);

  const completar = () => {
    if (!reporte.trim()) {
      alert('Debes escribir un reporte antes de completar el servicio.');
      return;
    }
    onCambiarEstado('completado', { reporte });
  };

  const asignarTecnico = (id) => {
    const t = tecnicos.find(x => x.id === Number(id));
    if (!t) return;
    onActualizar({ tecnicoAsignado: { id: t.id, nombre: t.nombre } });
    onCambiarEstado('asignado');
  };

  const agregarMaterial = () => {
    if (!nuevoMaterial.trim()) return;
    onActualizar({ materialesUsados: [...(servicio.materialesUsados || []), nuevoMaterial.trim()] });
    setNuevoMaterial('');
  };

  const quitarMaterial = (i) => {
    onActualizar({ materialesUsados: servicio.materialesUsados.filter((_, j) => j !== i) });
  };

  const btn = (bg, color = '#fff') => ({
    padding: '12px 16px', background: bg, color, border: 'none',
    borderRadius: 10, fontFamily: PP, fontWeight: 700, fontSize: 13, cursor: 'pointer', flex: 1,
  });

  return (
    <div style={overlay}>
      <div style={{ ...modalCard, maxWidth: 560 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 22 }}>{tipo.icono}</span>
              <div>
                <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 700 }}>{servicio.folio}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: COLOR_OSCURO }}>{servicio.cliente}{servicio.sucursal ? ` — ${servicio.sucursal}` : ''}</div>
              </div>
            </div>
          </div>
          <button onClick={onCerrar} style={{ background: 'transparent', border: 'none', fontSize: 24, cursor: 'pointer', color: COLOR_GRIS_CLARO, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, background: tipo.color + '22', color: tipo.color, padding: '4px 10px', borderRadius: 12 }}>
            {tipo.label}
          </span>
          <span style={{ fontSize: 11, fontWeight: 700, background: estado.color + '22', color: estado.color, padding: '4px 10px', borderRadius: 12 }}>
            {estado.label}
          </span>
          {excedido && (
            <span style={{ fontSize: 11, fontWeight: 800, background: COLOR_ROJO, color: '#fff', padding: '4px 10px', borderRadius: 12 }}>
              ⚠️ TIEMPO EXCEDIDO
            </span>
          )}
        </div>

        {/* TIMER */}
        {enProceso && (
          <div style={{
            background: excedido ? '#fde8e8' : '#eaf2fb',
            border: `2px solid ${excedido ? COLOR_ROJO : COLOR_PRINCIPAL}`,
            borderRadius: 12, padding: 14, marginBottom: 12, textAlign: 'center',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, marginBottom: 4 }}>TIEMPO TRANSCURRIDO</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: excedido ? COLOR_ROJO : COLOR_OSCURO, fontFamily: 'monospace' }}>
              {formatoMinutos(transcurrido)}
            </div>
            <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4 }}>
              Estimado: {formatoMinutos(servicio.tiempoEstimadoMinutos)}
            </div>
          </div>
        )}

        <div style={{ background: COLOR_FONDO, borderRadius: 10, padding: 12, marginBottom: 12, fontSize: 12, color: COLOR_GRIS, lineHeight: 1.8 }}>
          <div><strong>Descripción:</strong> {servicio.descripcion}</div>
          {servicio.equipoAfectado && <div><strong>Equipo:</strong> {servicio.equipoAfectado}</div>}
          <div><strong>Técnico:</strong> {servicio.tecnicoAsignado?.nombre || 'Sin asignar'}</div>
          {servicio.fechaProgramada && (
            <div><strong>Programado:</strong> {fechaCorta(servicio.fechaProgramada)} {servicio.horaProgramada && `· ${servicio.horaProgramada}`}</div>
          )}
          <div><strong>Costo:</strong> ${Number(servicio.costoServicio || 0).toLocaleString('es-MX', OPC_DINERO)} {!servicio.cobrar && '(no se cobra)'}</div>
          {servicio.garantia && <div><strong>Garantía:</strong> Sí</div>}
        </div>

        {/* HISTORIAL */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, marginBottom: 6 }}>LÍNEA DE TIEMPO</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 140, overflowY: 'auto' }}>
            {(servicio.historial || []).map((h, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, fontSize: 12 }}>
                <span style={{ color: COLOR_PRINCIPAL }}>•</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: COLOR_GRIS, fontWeight: 600 }}>{h.accion}</div>
                  <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO }}>{new Date(h.fecha).toLocaleString('es-MX')}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FOTOS */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, marginBottom: 6 }}>FOTOS</div>
          <button style={{
            width: '100%', padding: 14, background: '#fff',
            border: `2px dashed ${COLOR_PRINCIPAL}55`, borderRadius: 10,
            color: COLOR_OSCURO, cursor: 'pointer', fontFamily: PP, fontSize: 12, fontWeight: 600,
          }}>
            📷 Tomar foto / Subir
          </button>
        </div>

        {/* MATERIALES */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, marginBottom: 6 }}>MATERIALES USADOS</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 6 }}>
            {(servicio.materialesUsados || []).map((m, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', background: '#eaf2fb', padding: '6px 10px', borderRadius: 8 }}>
                <span style={{ flex: 1, fontSize: 12, color: COLOR_GRIS }}>{m}</span>
                <button onClick={() => quitarMaterial(i)} style={{ background: 'transparent', border: 'none', color: COLOR_ROJO, cursor: 'pointer', fontSize: 16 }}>×</button>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={nuevoMaterial}
              onChange={(e) => setNuevoMaterial(e.target.value)}
              placeholder="Agregar material..."
              style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #d8dde3', fontFamily: PP, fontSize: 12 }}
            />
            <button onClick={agregarMaterial} style={{ padding: '8px 14px', background: COLOR_PRINCIPAL, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontFamily: PP }}>+</button>
          </div>
        </div>

        {/* REPORTE */}
        {(['en_proceso', 'completado', 'validado', 'facturado'].includes(servicio.estado) || servicio.reporte) && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLOR_GRIS_CLARO, marginBottom: 6 }}>REPORTE DEL SERVICIO</div>
            <textarea
              value={reporte}
              onChange={(e) => setReporte(e.target.value)}
              onBlur={() => onActualizar({ reporte })}
              rows={3}
              placeholder="Describe el trabajo realizado..."
              disabled={['validado', 'facturado'].includes(servicio.estado)}
              style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid #d8dde3', fontFamily: PP, fontSize: 12, resize: 'vertical', boxSizing: 'border-box' }}
            />
          </div>
        )}

        {/* ACCIONES */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {servicio.estado === 'nuevo' && (
            <select
              onChange={(e) => e.target.value && asignarTecnico(e.target.value)}
              defaultValue=""
              style={{ flex: 1, padding: 12, borderRadius: 10, border: `1.5px solid ${COLOR_PRINCIPAL}`, fontFamily: PP, fontWeight: 700, color: COLOR_OSCURO, background: '#fff', cursor: 'pointer' }}
            >
              <option value="">Asignar técnico...</option>
              {tecnicos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
          )}
          {servicio.estado === 'asignado' && (
            <>
              <button onClick={() => onCambiarEstado('en_camino')} style={btn(COLOR_PRINCIPAL)}>🚗 En camino</button>
              <button onClick={() => onCambiarEstado('cancelado')} style={btn('#fff', COLOR_ROJO)}>Cancelar</button>
            </>
          )}
          {servicio.estado === 'en_camino' && (
            <button onClick={() => onCambiarEstado('en_sitio')} style={btn('#f4a261')}>📍 Llegó al sitio</button>
          )}
          {servicio.estado === 'en_sitio' && (
            <button onClick={() => onCambiarEstado('en_proceso')} style={btn('#9b59b6')}>▶ Iniciar servicio</button>
          )}
          {servicio.estado === 'en_proceso' && (
            <button onClick={completar} style={btn('#1D9E75')}>✓ Completar servicio</button>
          )}
          {servicio.estado === 'completado' && puedeValidar && (
            <button onClick={() => onCambiarEstado('validado')} style={btn(COLOR_OSCURO)}>✔ Validar</button>
          )}
          {servicio.estado === 'validado' && (
            <button onClick={() => onCambiarEstado('facturado')} style={btn('#1D9E75')}>💵 Facturar</button>
          )}
        </div>
      </div>
    </div>
  );
}

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(20,30,50,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, padding: 16,
};
const modalCard = {
  background: '#fff', borderRadius: 16, padding: 22,
  width: '100%', maxHeight: 'calc(var(--beep-vh) * 92)', overflowY: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.25)', fontFamily: PP,
};

export default Servicios;
